import 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Spinner from '@scalero-inc/forno-spinner';
import { jsx, jsxs } from 'react/jsx-runtime';

function AvatarFallback(_ref) {
  var firstName = _ref.firstName,
      lastName = _ref.lastName,
      prefix = _ref.prefix;
  return /*#__PURE__*/jsx("div", {
    className: prefix,
    children: "".concat(firstName[0] || '-').concat(lastName[0] || '-')
  });
}

AvatarFallback.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  prefix: PropTypes.string.isRequired
};
AvatarFallback.defaultProps = {
  firstName: '',
  lastName: ''
};

function AvatarImage(_ref) {
  var image = _ref.image,
      firstName = _ref.firstName,
      lastName = _ref.lastName,
      prefix = _ref.prefix;
  return image ? /*#__PURE__*/jsx("img", {
    className: prefix,
    src: image,
    alt: "".concat(firstName || '', " ").concat(lastName || '')
  }) : /*#__PURE__*/jsx(AvatarFallback, {
    prefix: prefix,
    firstName: firstName,
    lastName: lastName
  });
}

AvatarImage.propTypes = {
  image: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  prefix: PropTypes.string.isRequired
};
AvatarImage.defaultProps = {
  image: null,
  firstName: '',
  lastName: ''
};

function AvatarTeamFallback(_ref) {
  var prefix = _ref.prefix,
      teamName = _ref.teamName;
  return /*#__PURE__*/jsx("div", {
    className: "".concat(prefix, "-logo"),
    children: teamName ? "".concat(teamName[0]) : '-'
  });
}

AvatarTeamFallback.propTypes = {
  teamName: PropTypes.string,
  prefix: PropTypes.string.isRequired
};
AvatarTeamFallback.defaultProps = {
  teamName: ''
};

function AvatarTeam(_ref) {
  var prefix = _ref.prefix,
      logo = _ref.logo,
      teamName = _ref.teamName;
  return logo ? /*#__PURE__*/jsx("img", {
    className: "".concat(prefix, "-logo"),
    src: logo,
    alt: teamName
  }) : /*#__PURE__*/jsx(AvatarTeamFallback, {
    prefix: prefix,
    teamName: teamName
  });
}

AvatarTeam.propTypes = {
  teamName: PropTypes.string,
  logo: PropTypes.string,
  prefix: PropTypes.string.isRequired
};
AvatarTeam.defaultProps = {
  teamName: '',
  logo: null
};

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function BaseAvatar(props) {
  return /*#__PURE__*/jsx("div", _objectSpread2({}, props));
}

var sizes = ['xs', 's', 'm', 'l'];

var getSizeInRem = function getSizeInRem(size) {
  var auxSize = size;

  if (typeof size === 'string') {
    if (size === 'l') auxSize = '10';
    if (size === 'm') auxSize = '08';
    if (size === 's') auxSize = '06';
    if (size === 'xs') auxSize = '04';
  }

  return "var(--size-".concat(auxSize, ")");
};
var getDimensions = function getDimensions() {
  var size = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  return {
    width: getSizeInRem(size),
    height: getSizeInRem(size)
  } || {};
};

var prefix = 'forno-avatar';
/**
 * Renders an `<Avatar/>` component
 * @param {Object} props
 * @param {string} [props.className=] - Custom class name
 * @param {string} [props.firstName=] - User's first name value
 * @param {string} [props.lastName=] - User's last name value
 * @param {string} [props.teamName=] - User's team name value
 * @param {string} [props.logo=] - User's team logo image file source
 * @param {string} [props.src=] - User's avatar image file source
 * @param {(string|number)} [props.size=l] - [s, m, l] or the number of px for width
 * @param {boolean} [props.loading=] - Show a spinner instead of avatar image
 * @param {boolean} [props.round=] - Turn shape of avatar round
 * @param {boolean} [props.withBorder=false] - Add a border to avatar
 */

function Avatar(_ref) {
  var firstName = _ref.firstName,
      lastName = _ref.lastName,
      src = _ref.src,
      size = _ref.size,
      loading = _ref.loading,
      className = _ref.className,
      withLogo = _ref.withLogo,
      teamName = _ref.teamName,
      logo = _ref.logo,
      withBorder = _ref.withBorder;
  var typography = withLogo ? 'font-block-s-sb' : "font-block-".concat(size, "-sb");
  var customClassName = classNames("".concat(prefix, "-wrapper"), className, typography);
  var dimensions = getDimensions(size);
  return /*#__PURE__*/jsxs(BaseAvatar, {
    className: customClassName,
    style: dimensions,
    "data-size": size,
    "data-with-logo": withLogo,
    "data-testid": prefix,
    "data-with-border": withBorder,
    children: [loading ? /*#__PURE__*/jsx(Spinner, {}) : null, !loading ? /*#__PURE__*/jsx(AvatarImage, {
      image: src,
      firstName: firstName,
      lastName: lastName,
      prefix: prefix,
      size: size
    }) : null, !loading && withLogo ? /*#__PURE__*/jsx(AvatarTeam, {
      prefix: prefix,
      logo: logo,
      teamName: teamName
    }) : null]
  });
}

Avatar.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.oneOf(sizes), PropTypes.number]),
  src: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  loading: PropTypes.bool,
  withLogo: PropTypes.bool,
  teamName: PropTypes.string,
  logo: PropTypes.string,
  withBorder: PropTypes.bool
};
Avatar.defaultProps = {
  className: null,
  src: null,
  loading: false,
  firstName: '',
  lastName: '',
  size: 'l',
  withLogo: false,
  teamName: '',
  logo: null,
  withBorder: false
};

export { Avatar as default, prefix };
